import { useEffect, useState } from "react";
import { apiRequest } from "../utils/api";

/* The five parts of a car rating, in the order they are asked and shown. The same keys
   the server averages into each car's avgCriteria, which the list's "On time",
   "Polite" and other filters read. */
export const RATING_PARTS = ["punctuality", "driverBehaviour", "rideComfort", "cleanliness", "safety"];

/* One enquiry's stars, for the customer who made it - shared by the enquiry page
   (components/LeadDetailSheet), the home lists' "Your recent enquiry" card and the car's
   own page (pages/CarDetail), so none of them can ask differently or record differently.

   It reads what they gave before, if anything, so no screen asks twice, and sends new
   stars the way the rest of the app does: POST /ratings with the car (or, for a hire,
   the driver) as the target. The server only takes a rating from a customer who has an
   enquiry with that partner - which an enquiry is.

   withParts: the car page also asks for the five parts (partKeys: the hire driver's
   page asks for its own four). They start at the stars the customer picked, so a
   customer who changes nothing still sends a whole rating, and the filters built on
   the parts keep being fed. The server makes the overall score the average of the
   parts.

   Every form also asks whether what came was what they chose - "Did the same car you
   booked come?", "Did the same driver you booked come?" (components/
   RatingMatchQuestions). Yes, no, or skipped; a hire is asked only about the driver,
   the car being the customer's own, and nobody is asked after saying they did not
   book. The answers are counted into the car's rating for other customers to see. */
export default function useLeadRating(lead, t, { withParts = false, partKeys, raterName, onSaved } = {}) {
  const isHire = lead?.sourceType === "HIRE_DRIVER";
  const target = lead?.carId ? { targetId: lead.carId, targetType: isHire ? "PERSONAL_DRIVER" : "CAR" } : null;
  const targetId = target?.targetId || null;
  const keys = partKeys || (withParts ? RATING_PARTS : null);
  const travelled = lead?.customerOutcome !== "LOST";
  const asks = { car: Boolean(target) && !isHire && travelled, driver: Boolean(target) && travelled };

  const [myRating, setMyRating] = useState(null);
  // The rating as the server holds it, so "Change" opens with what they gave.
  const [saved, setSaved] = useState(null);
  const [editing, setEditing] = useState(false);
  const [stars, setStars] = useState(0);
  const [parts, setParts] = useState(null);
  const [review, setReview] = useState("");
  // Same car, same driver: true, false, or null for not answered.
  const [sameCar, setSameCar] = useState(null);
  const [sameDriver, setSameDriver] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!targetId) return undefined;
    let cancelled = false;
    apiRequest(`/ratings/me/${encodeURIComponent(targetId)}`)
      .then((r) => {
        if (cancelled || !r?.rating?.score) return;
        setMyRating(Math.round(Number(r.rating.score)));
        setSaved(r.rating);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [targetId]);

  const allParts = (n) => Object.fromEntries((keys || []).map((k) => [k, n]));

  // The overall stars; with parts, every part starts at the same number.
  const pick = (n) => {
    setStars(n);
    if (keys) setParts(allParts(n));
  };
  const setPart = (k, n) => setParts((p) => ({ ...(p || allParts(stars)), [k]: n }));

  // "Change" after rating: the form again, holding what they sent last time.
  const edit = () => {
    const n = myRating || 0;
    setStars(n);
    if (keys) {
      setParts(Object.fromEntries(keys.map((k) => [k, Number(saved?.criteria?.[k]) || n])));
    }
    setReview(saved?.review || "");
    setSameCar(typeof saved?.sameCar === "boolean" ? saved.sameCar : null);
    setSameDriver(typeof saved?.sameDriver === "boolean" ? saved.sameDriver : null);
    setError("");
    setEditing(true);
  };

  const send = async () => {
    if (!target || !stars || busy) return;
    setBusy(true);
    setError("");
    try {
      const body = { ...target, score: stars, review: review.trim().slice(0, 300) || undefined };
      if (keys && parts) body.criteria = parts;
      if (raterName) body.raterName = raterName;
      // Only an answer is sent; a question left alone stays unanswered.
      if (asks.car && typeof sameCar === "boolean") body.sameCar = sameCar;
      if (asks.driver && typeof sameDriver === "boolean") body.sameDriver = sameDriver;
      await apiRequest("/ratings", { method: "POST", body: JSON.stringify(body) });
      const vals = body.criteria ? Object.values(body.criteria) : [];
      const score = vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : stars;
      setMyRating(Math.round(score));
      setSaved({ score, criteria: body.criteria || null, review: body.review || "",
        sameCar: body.sameCar ?? null, sameDriver: body.sameDriver ?? null });
      setEditing(false);
      onSaved?.();
    } catch {
      // In the reader's language: the server's refusal is written in English.
      setError(t("ld_rateErr"));
    } finally {
      setBusy(false);
    }
  };

  return {
    target, myRating, stars, setStars, pick, parts, setPart, review, setReview, busy, error, send, editing, edit,
    asks, sameCar, setSameCar, sameDriver, setSameDriver,
  };
}
